import { Injectable } from '@angular/core';

const NAMESPACES = [
  'preferences',
  'auth',
  'message_box',
  'company_browser_filter_collapse_state',
  'maintenance',
  'report',
  'geo_geometry',
  'adverse_event',
  'admin',
  'network_risk_score',
  'dashboard',
] as const;
type Namespace = (typeof NAMESPACES)[number];

type UserPreferenceKeys =
  | 'company_accounts_view.account_number_denomination_option'
  | 'company_accounts_view.current_lang'
  | 'company_accounts_view.descending_accounting_numbers';
// for the future refactoring, not in use yet
type AuthKeys = 'user_email' | 'login_message' | 'skip_check_session' | 'redirect_url';
type MessageBoxKeys = 'closed_keys';
type CompanyBrowserFilterCollapseStateKeys = string;
type MaintenanceKeys = 'redirect_back_url';
type ReportKeys = 'property' | 'screening' | 'screening_case_review';
type GeoGeometryKeys = 'property';
type AdverseMediaKeys = 'filters';
type AdminKeys = 'navbar_minified';
type NetworkRiskScoreKeys = 'network_risk_score_default_states';
type DashboardKeys = 'user_filters' | 'user_initial_setup_completed';

type NamespaceKey =
  | UserPreferenceKeys
  | AuthKeys
  | MessageBoxKeys
  | CompanyBrowserFilterCollapseStateKeys
  | MaintenanceKeys
  | ReportKeys
  | GeoGeometryKeys
  | AdminKeys
  | NetworkRiskScoreKeys
  | DashboardKeys;

type NamespaceKeySet<Prefix extends Namespace, Key extends NamespaceKey> = `${Prefix}.${Key}`;

type UserPreferencesKey = NamespaceKeySet<`preferences`, UserPreferenceKeys>;
type AuthKey = NamespaceKeySet<'auth', AuthKeys>;
type MessageBox = NamespaceKeySet<'message_box', MessageBoxKeys>;
type CompanyBrowserFilterCollapseState = NamespaceKeySet<
  'company_browser_filter_collapse_state',
  CompanyBrowserFilterCollapseStateKeys
>;
type MaintenanceKey = NamespaceKeySet<'maintenance', MaintenanceKeys>;
type ReportKey = NamespaceKeySet<'report', ReportKeys>;
type GeoGeometryKey = NamespaceKeySet<'geo_geometry', GeoGeometryKeys>;
type AdverseMediaKey = NamespaceKeySet<'adverse_event', AdverseMediaKeys>;
type AdminKey = NamespaceKeySet<'admin', AdminKeys>;
type NetworkRiskScoreKey = NamespaceKeySet<'network_risk_score', NetworkRiskScoreKeys>;
type DashboardKey = NamespaceKeySet<'dashboard', DashboardKeys>;

export type StorageKey =
  | UserPreferencesKey
  | AuthKey
  | MessageBox
  | CompanyBrowserFilterCollapseState
  | MaintenanceKey
  | ReportKey
  | GeoGeometryKey
  | AdverseMediaKey
  | AdminKey
  | NetworkRiskScoreKey
  | DashboardKey;

const STORAGE_PREFIX = 'enin';

export function isLocalStorageAvailable(): boolean {
  const test = '__local_storage_test_string_58826e30747b432e872bf49d972dd8e8__';
  try {
    localStorage.setItem(test, test);
    localStorage.removeItem(test);
    return true;
  } catch {
    return false;
  }
}

function setLocalStorageItem<T>(key: StorageKey, value: T): void {
  if (isLocalStorageAvailable()) {
    localStorage.setItem(`${STORAGE_PREFIX}.${key}`, JSON.stringify(value));
  }
}

function getLocalStorageItem(key: StorageKey): unknown | null {
  if (!isLocalStorageAvailable()) {
    return null;
  }
  const unserializedValue = localStorage.getItem(`${STORAGE_PREFIX}.${key}`);
  try {
    if (typeof unserializedValue === 'string') {
      return JSON.parse(unserializedValue);
    } else {
      return unserializedValue;
    }
  } catch {
    return unserializedValue;
  }
}

/**
 * An interface for work with localStorage from Enin apps
 */
@Injectable({
  providedIn: 'root',
})
export class StorageService {
  public setItem<T>(key: StorageKey, value: T): void {
    setLocalStorageItem<T>(key, value);
  }

  public getItem<T>(key: StorageKey, fallbackValue: T | null = null): unknown {
    const item = getLocalStorageItem(key);
    return item === null && fallbackValue !== null ? fallbackValue : item;
  }

  public clearNamespace(namespace: Namespace): void {
    const remove: string[] = Object.keys(localStorage).filter((key) =>
      key.startsWith(`${STORAGE_PREFIX}.${namespace}`),
    );
    for (const key of remove) {
      localStorage.removeItem(key);
    }
  }

  public clearAllExceptNamespaces(): void {
    const matcher = new RegExp(NAMESPACES.map((i) => `^${STORAGE_PREFIX}.${i}`).join('|'), 'gi');
    const remove: string[] = Object.keys(localStorage).filter((key) => !key.match(matcher));
    for (const key of remove) {
      localStorage.removeItem(key);
    }
  }
}
